import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { value: 'all', label: 'All Languages' },
  { value: 'english', label: 'EN — English' },
  { value: 'finnish', label: 'FI — Finnish' },
  { value: 'spanish', label: 'ES — Spanish' },
  { value: 'german', label: 'DE — German' },
];

const emptyCampaign = { name: '', subject: '', business_id: '', list_id: '', html_content: '', json_content: '' };

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCampaign);
  const [sendDialog, setSendDialog] = useState(null);
  const [sendLang, setSendLang] = useState('all');
  const [sendMode, setSendMode] = useState('instant');
  const [dailyLimit, setDailyLimit] = useState(30);

  const fetchAll = useCallback(async () => {
    try {
      const [cRes, bRes, lRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { withCredentials: true }),
        axios.get(`${API}/businesses`, { withCredentials: true }),
        axios.get(`${API}/lists`, { withCredentials: true }),
      ]);
      setCampaigns(cRes.data);
      setBusinesses(bRes.data);
      setLists(lRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/campaigns`, { ...form, status: 'draft' }, { withCredentials: true });
      toast.success('Campaign created');
      setOpen(false);
      setForm(emptyCampaign);
      navigate(`/campaigns/${res.data.campaign_id}/edit`);
    } catch (e) { toast.error('Failed to create campaign'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { withCredentials: true });
      toast.success('Campaign deleted');
      fetchAll();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleSend = async (id) => {
    const params = new URLSearchParams();
    if (sendLang && sendLang !== 'all') params.set('language', sendLang);
    if (sendMode === 'throttled' && dailyLimit > 0) params.set('daily_limit', dailyLimit);
    const qs = params.toString() ? `?${params.toString()}` : '';
    try {
      const res = await axios.post(`${API}/campaigns/${id}/send${qs}`, {}, { withCredentials: true });
      if (res.data.status === 'sending_throttled') {
        toast.success(res.data.message);
      } else {
        toast.success(`Campaign sent! ${res.data.sent_count} emails sent, ${res.data.failed_count} failed`);
      }
      setSendDialog(null);
      setSendLang('all');
      setSendMode('instant');
      setDailyLimit(30);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send campaign');
    }
  };

  const getBizName = (id) => businesses.find(b => b.business_id === id)?.name || '—';
  const getListName = (id) => lists.find(l => l.list_id === id)?.name || '—';

  return (
    <div className="flex-1 p-8" data-testid="campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Campaigns</h1>
          <p className="text-[#B0B0B0] text-sm mt-1">{campaigns.length} campaigns</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-campaign-btn" className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md">
              <Plus size={16} className="mr-2" /> New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>New Campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Campaign Name</Label>
                <Input data-testid="campaign-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="March Newsletter" />
              </div>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Subject Line</Label>
                <Input data-testid="campaign-subject-input" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Your monthly update" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Business</Label>
                  <Select value={form.business_id} onValueChange={v => setForm(p => ({ ...p, business_id: v }))}>
                    <SelectTrigger data-testid="campaign-business-select" className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                      {businesses.map(b => (
                        <SelectItem key={b.business_id} value={b.business_id} className="text-white hover:bg-[#1A1A1A]">{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Mailing List</Label>
                  <Select value={form.list_id} onValueChange={v => setForm(p => ({ ...p, list_id: v }))}>
                    <SelectTrigger data-testid="campaign-list-select" className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                      {lists.map(l => (
                        <SelectItem key={l.list_id} value={l.list_id} className="text-white hover:bg-[#1A1A1A]">{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="create-campaign-submit" onClick={handleCreate} className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                Create & Design Email
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {campaigns.map((c, i) => (
          <div key={c.campaign_id} className={`bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5 animate-fade-in stagger-${(i % 4) + 1}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold">{c.name}</h3>
                <p className="text-xs text-[#808080] mt-0.5">{c.subject}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${
                c.status === 'sent' ? 'bg-[#00FF99]/10 text-[#00FF99]' :
                c.status === 'sending_throttled' ? 'bg-[#007AFF]/10 text-[#007AFF]' :
                c.status === 'draft' ? 'bg-[#FFB800]/10 text-[#FFB800]' :
                'bg-[#808080]/10 text-[#808080]'
              }`}>
                {c.status === 'sending_throttled' ? 'Sending...' : c.status}
              </span>
            </div>
            <div className="space-y-1 mb-4">
              <p className="text-xs text-[#808080]">Business: {getBizName(c.business_id)}</p>
              <p className="text-xs text-[#808080]">List: {getListName(c.list_id)}</p>
              {c.status === 'sent' && (
                <p className="text-xs text-[#00FF99]">Sent: {c.sent_count || 0} | Failed: {c.failed_count || 0}</p>
              )}
              {c.status === 'sending_throttled' && (
                <div className="space-y-1">
                  <p className="text-xs text-[#007AFF]">
                    {c.throttle_sent_count || 0}/{c.throttle_total || 0} sent ({c.daily_limit || 0}/day)
                  </p>
                  <div className="w-full h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div className="h-full bg-[#007AFF] rounded-full transition-all duration-500"
                      style={{ width: `${c.throttle_total ? ((c.throttle_sent_count || 0) / c.throttle_total * 100) : 0}%` }} />
                  </div>
                  {c.throttle_total && c.daily_limit && (
                    <p className="text-[10px] text-[#808080]">
                      ~{Math.ceil((c.throttle_total - (c.throttle_sent_count || 0)) / c.daily_limit)} days remaining
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {c.status === 'draft' && (
                <>
                  <Button size="sm" onClick={() => navigate(`/campaigns/${c.campaign_id}/edit`)}
                    className="bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99] hover:text-[#00FF99] text-xs">
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" onClick={() => { setSendDialog(c.campaign_id); setSendLang('all'); }}
                    data-testid={`send-campaign-${c.campaign_id}`}
                    className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] text-xs">
                    <Send size={12} className="mr-1" /> Send
                  </Button>
                </>
              )}
              {c.status === 'sent' && (
                <Button size="sm" onClick={() => navigate(`/campaigns/${c.campaign_id}/edit`)}
                  className="bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99] text-xs">
                  <Eye size={12} className="mr-1" /> View
                </Button>
              )}
              {c.status === 'sending_throttled' && (
                <Button size="sm" onClick={() => navigate(`/campaigns/${c.campaign_id}/edit`)}
                  className="bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#007AFF] text-xs">
                  <Eye size={12} className="mr-1" /> View
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => handleDelete(c.campaign_id)}
                className="text-[#B0B0B0] hover:text-[#FF3B30] text-xs ml-auto">
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        ))}
        {campaigns.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#808080]">
            <Send size={32} className="mx-auto mb-3 opacity-50" />
            <p>No campaigns yet. Create your first email campaign.</p>
          </div>
        )}
      </div>

      {/* Send Campaign Dialog with Language Filter + Throttle */}
      <Dialog open={!!sendDialog} onOpenChange={(v) => { if (!v) { setSendDialog(null); setSendLang('all'); setSendMode('instant'); setDailyLimit(30); } }}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Send Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#B0B0B0]">Configure how this campaign is sent to your contacts.</p>

            {/* Language Filter */}
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Target Language</Label>
              <Select value={sendLang} onValueChange={setSendLang}>
                <SelectTrigger data-testid="send-language-select" className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                  {LANGUAGES.map(l => (
                    <SelectItem key={l.value} value={l.value} className="text-white hover:bg-[#1A1A1A]">{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Send Mode */}
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Send Mode</Label>
              <div className="flex gap-2 mt-1">
                <button onClick={() => setSendMode('instant')} data-testid="send-mode-instant"
                  className={`flex-1 px-3 py-2.5 rounded text-xs text-center transition-colors duration-200 border ${sendMode === 'instant' ? 'bg-[#00FF99]/10 text-[#00FF99] border-[#00FF99]' : 'bg-[#1A1A1A] text-[#B0B0B0] border-[rgba(255,255,255,0.12)]'}`}>
                  <span className="font-bold block">Send All Now</span>
                  <span className="text-[10px] opacity-60 mt-0.5 block">All emails at once</span>
                </button>
                <button onClick={() => setSendMode('throttled')} data-testid="send-mode-throttled"
                  className={`flex-1 px-3 py-2.5 rounded text-xs text-center transition-colors duration-200 border ${sendMode === 'throttled' ? 'bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]' : 'bg-[#1A1A1A] text-[#B0B0B0] border-[rgba(255,255,255,0.12)]'}`}>
                  <span className="font-bold block">Daily Batches</span>
                  <span className="text-[10px] opacity-60 mt-0.5 block">Spread over days</span>
                </button>
              </div>
            </div>

            {/* Daily Limit (only for throttled) */}
            {sendMode === 'throttled' && (
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Emails Per Day</Label>
                <Input data-testid="daily-limit-input" type="number" min={1} max={500} value={dailyLimit}
                  onChange={e => setDailyLimit(parseInt(e.target.value) || 30)}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" />
                <p className="text-xs text-[#808080] mt-1">
                  Recommended: 20-50/day for cold lists to avoid spam filters
                </p>
              </div>
            )}

            <Button onClick={() => handleSend(sendDialog)} data-testid="confirm-send-btn"
              className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
              <Send size={14} className="mr-2" />
              {sendMode === 'throttled' ? `Start Sending (${dailyLimit}/day)` : 'Send Now'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
